import { ref } from 'vue'
import { defineStore } from 'pinia'

import { ADMIN_SESSION_CACHE_KEYS } from '@/shared/constants/cache-keys'

export interface AdminNavigationTab {
  path: string
  title: string
  titleKey?: string
  closable: boolean
}

const dashboardTab: AdminNavigationTab = {
  path: '/admin/dashboard',
  title: '工作台',
  titleKey: 'admin.nav.dashboard',
  closable: false
}

const getStorage = (): Storage | null => {
  return typeof sessionStorage === 'undefined' ? null : sessionStorage
}

const isValidTab = (value: unknown): value is AdminNavigationTab => {
  if (!value || typeof value !== 'object') {
    return false
  }
  const tab = value as Partial<AdminNavigationTab>
  return typeof tab.path === 'string' &&
    tab.path.startsWith('/admin/') &&
    typeof tab.title === 'string' &&
    (tab.titleKey === undefined || typeof tab.titleKey === 'string') &&
    typeof tab.closable === 'boolean'
}

const readTabs = (): AdminNavigationTab[] => {
  const stored = getStorage()?.getItem(ADMIN_SESSION_CACHE_KEYS.tabs)
  if (!stored) {
    return [{ ...dashboardTab }]
  }

  try {
    const parsed = JSON.parse(stored) as unknown
    const tabs = Array.isArray(parsed) ? parsed.filter(isValidTab) : []
    const uniqueTabs = tabs.filter((tab, index) => {
      return tabs.findIndex(candidate => candidate.path === tab.path) === index
    })
    return [
      { ...dashboardTab },
      ...uniqueTabs.filter(tab => tab.path !== dashboardTab.path)
    ]
  } catch {
    return [{ ...dashboardTab }]
  }
}

export const useAdminNavigationStore = defineStore('admin-navigation', () => {
  const tabs = ref<AdminNavigationTab[]>(readTabs())
  const refreshRevision = ref(0)

  const persist = (): void => {
    getStorage()?.setItem(ADMIN_SESSION_CACHE_KEYS.tabs, JSON.stringify(tabs.value))
  }

  const openTab = (tab: Omit<AdminNavigationTab, 'closable'> & { closable?: boolean }): void => {
    const existing = tabs.value.find(item => item.path === tab.path)
    if (existing) {
      existing.title = tab.title
      existing.titleKey = tab.titleKey
    } else {
      tabs.value.push({
        path: tab.path,
        title: tab.title,
        titleKey: tab.titleKey,
        closable: tab.closable ?? tab.path !== dashboardTab.path
      })
    }
    persist()
  }

  const closeTab = (path: string, activePath: string): string | null => {
    const index = tabs.value.findIndex(tab => tab.path === path)
    if (index < 0 || !tabs.value[index]?.closable) {
      return null
    }

    tabs.value.splice(index, 1)
    persist()
    if (path !== activePath) {
      return null
    }

    return tabs.value[Math.max(0, index - 1)]?.path || dashboardTab.path
  }

  const closeOtherTabs = (activePath: string): string => {
    const activeTab = tabs.value.find(tab => tab.path === activePath)
    tabs.value = [
      { ...dashboardTab },
      ...(activeTab && activeTab.path !== dashboardTab.path ? [activeTab] : [])
    ]
    persist()
    return activeTab?.path || dashboardTab.path
  }

  const closeAllTabs = (): string => {
    tabs.value = [{ ...dashboardTab }]
    persist()
    return dashboardTab.path
  }

  const refreshCurrent = (): void => {
    refreshRevision.value += 1
  }

  const reset = (): void => {
    tabs.value = [{ ...dashboardTab }]
    refreshRevision.value = 0
    getStorage()?.removeItem(ADMIN_SESSION_CACHE_KEYS.tabs)
  }

  return {
    tabs,
    refreshRevision,
    openTab,
    closeTab,
    closeOtherTabs,
    closeAllTabs,
    refreshCurrent,
    reset
  }
})
